import { useCallback, useEffect } from 'react';
import { useRecoilValue, useRecoilState } from 'recoil';
import shader from 'shader';
import { EXPANDED_CHAT_WIDTH } from '@/config/constants';
import { backgroundColorSelector, accentColorSelector, userConfigState, chatWindowSizeSelector, fontFamilySelector, variantSelector, themeColorsSelector, currentThemeOverridesSelector, } from '@/state/config';
export const variables = {
    '--moveo-z-index': '1100',
    '--moveo-shadow-message': '0 2px 2px rgba(0 0 0 4%)',
    '--moveo-shadow-bubble': '0px 0px 10px 0px #10182824',
    '--moveo-border-radius-base': '5px',
    '--moveo-font-primary': "'Inter', monospace",
    '--moveo-survey-overlay': '#8c8c8c',
    '--moveo-header-height': '55px',
    '--moveo-header-background': '', // Will be set dynamically via setCSSVariables or theme
    '--moveo-header-text-color': '', // Will be set dynamically via setCSSVariables or theme
    '--moveo-modal-tall-height': '600px',
    '--moveo-modal-compact-min-height': '112px',
    '--moveo-modal-phone-min-height': '54vh',
    '--moveo-modal-tall-width': 'clamp(300px, 400px, 450px)',
    '--moveo-modal-full-height': '80vh',
    '--moveo-modal-full-width': '80vw',
    '--moveo-modal-full-height-phone': '60vh',
    '--moveo-modal-header-height': '46px',
    '--moveo-modal-background-color': '#fff',
    '--moveo-webview-message-width': '220px',
    '--moveo-webview-message-height': '30px',
    '--moveo-webview-button-width': '80%',
    '--moveo-loadingIcon-size': '1rem',
    '--moveo-carousel-media-height': '130px',
    '--moveo-message-max-width': '251px',
    '--moveo-message-max-width-phone': '288px',
    '--moveo-message-min-width': '70px',
    '--moveo-dropdown-box-shadow': '0.3px 0.5px 0.7px hsl(0deg 0% 55% / 45%), 1.2px 1.8px 2.4px -0.8px hsl(0deg 0% 55% / 45%), 3px 4.7px 6.2px -1.7px hsl(0deg 0% 55% / 45%), 7.3px 11.6px 15.3px -2.5px hsl(0deg 0% 55% / 44%)',
    '--moveo-dropdown-width': '223px',
    '--moveo-survey-width': '89%',
    '--moveo-survey-max-width': '50%',
    '--moveo-survey-height': '80%',
    '--moveo-font-xs': '0.625rem',
    '--moveo-font-sm': '0.75rem',
    '--moveo-font-md': '0.875rem',
    '--moveo-font-lg': '1rem',
    '--nbg-bg': '#f5f8f6',
    //TODO check rename/replace old variables
    // New variables
    '--moveo-new-header-height': '68px',
    '--moveo-new-border-radius-base': '16px',
    '--moveo-expanded-chat-width': EXPANDED_CHAT_WIDTH,
};
const googleFontPath = (fontFamily) => `https://fonts.googleapis.com/css2?family=${fontFamily}:wght@300;400;600;700&display=swap`;
const configs = {
    nbg: [
        {
            fontFamily: 'Aptos',
            fontPath: '../public/fonts/aptos/Aptos-Bold-Italic.ttf',
            fontWeight: '700',
            fontStyle: 'italic',
        },
        {
            fontFamily: 'Aptos',
            fontPath: '../public/fonts/aptos/Aptos-Bold.ttf',
            fontWeight: '700',
            fontStyle: 'normal',
        },
        {
            fontFamily: 'Aptos',
            fontPath: '../public/fonts/aptos/Aptos-ExtraBold-Italic.ttf',
            fontWeight: '800',
            fontStyle: 'italic',
        },
        {
            fontFamily: 'Aptos',
            fontPath: '../public/fonts/aptos/Aptos-ExtraBold.ttf',
            fontWeight: '800',
            fontStyle: 'normal',
        },
        {
            fontFamily: 'Aptos',
            fontPath: '../public/fonts/aptos/Aptos-Italic.ttf',
            fontWeight: '400',
            fontStyle: 'italic',
        },
        {
            fontFamily: 'Aptos',
            fontPath: '../public/fonts/aptos/Aptos-Light-Italic.ttf',
            fontWeight: '300',
            fontStyle: 'italic',
        },
        {
            fontFamily: 'Aptos',
            fontPath: '../public/fonts/aptos/Aptos-Light.ttf',
            fontWeight: '300',
            fontStyle: 'normal',
        },
        {
            fontFamily: 'Aptos',
            fontPath: '../public/fonts/aptos/Aptos-SemiBold-Italic.ttf',
            fontWeight: '600',
            fontStyle: 'italic',
        },
        {
            fontFamily: 'Aptos',
            fontPath: '../public/fonts/aptos/Aptos-SemiBold.ttf',
            fontWeight: '600',
            fontStyle: 'normal',
        },
    ],
    alpha: [
        {
            fontFamily: 'AlphaText',
            fontPath: '../public/fonts/alpha/AlphaText-Regular.woff',
            fontWeight: '400',
            fontStyle: 'normal',
        },
        {
            fontFamily: 'AlphaText',
            fontPath: '../public/fonts/alpha/AlphaText-Italic.woff',
            fontWeight: '400',
            fontStyle: 'italic',
        },
        {
            fontFamily: 'AlphaText',
            fontPath: '../public/fonts/alpha/AlphaText-Light.woff',
            fontWeight: '300',
            fontStyle: 'normal',
        },
        {
            fontFamily: 'AlphaText',
            fontPath: '../public/fonts/alpha/AlphaText-LightItalic.woff',
            fontWeight: '300',
            fontStyle: 'italic',
        },
        {
            fontFamily: 'AlphaText',
            fontPath: '../public/fonts/alpha/AlphaText-Medium.woff',
            fontWeight: '500',
            fontStyle: 'normal',
        },
        {
            fontFamily: 'AlphaText',
            fontPath: '../public/fonts/alpha/AlphaText-MediumItalic.woff',
            fontWeight: '500',
            fontStyle: 'italic',
        },
        {
            fontFamily: 'AlphaText',
            fontPath: '../public/fonts/alpha/AlphaText-SemiBold.woff',
            fontWeight: '600',
            fontStyle: 'normal',
        },
        {
            fontFamily: 'AlphaText',
            fontPath: '../public/fonts/alpha/AlphaText-SemiBoldItalic.woff',
            fontWeight: '600',
            fontStyle: 'italic',
        },
        {
            fontFamily: 'AlphaText',
            fontPath: '../public/fonts/alpha/AlphaText-Bold.woff',
            fontWeight: '700',
            fontStyle: 'normal',
        },
        {
            fontFamily: 'AlphaText',
            fontPath: '../public/fonts/alpha/AlphaText-BoldItalic.woff',
            fontWeight: '700',
            fontStyle: 'italic',
        },
    ],
    default: {
        fontFamily: 'Inter',
        fontPath: googleFontPath('Inter'),
    },
};
/**
 * Maps ThemeColors config fields to CSS variable names.
 * This mapping allows the backend config to control CSS variables.
 */
const themeColorsToCssVars = {
    accent_color: '--color-accent',
    background_color: '--color-background',
    header_color: '--moveo-header-background',
    border_color: '--color-border',
    text_color_user: '--color-text-primary-foreground',
    text_color_agent: '--color-text-secondary-foreground',
    text_color_header: '--moveo-header-text-color',
    launcher_background: '--color-launcher-background',
    launcher_foreground: '--color-launcher-foreground',
    rating_background: '--color-rating-background',
    send_icon_color: '--color-send-icon',
    link_color: '--color-link',
    swimmlane_background: '--color-swimmlane-background',
    swimmlane_foreground: '--color-swimmlane-foreground',
};
/**
 * Maps semantic font size names to rem values for brain messages.
 * Users can pass 'small', 'medium', or 'large' instead of direct values.
 */
const brainMessageFontSizes = {
    small: '0.875rem', // 14px
    medium: '1rem', // 16px
    large: '1.125rem', // 18px
};
/**
 * Applies CSS variables to an element with the following priority:
 * 1. Legacy flat colors (background_color, accent_color) - fallback
 * 2. theme_colors from config - overrides legacy if provided
 * 3. cssOverrides from setCSSVariables() API - highest priority
 */
export const applyCssVariables = ({ element, backgroundColor, accentColor, themeColors, cssOverrides, }) => {
    // 1. Apply legacy flat colors (fallback)
    if (backgroundColor) {
        element.style.setProperty('--moveo-background-color', backgroundColor);
        element.style.setProperty('--color-accent', backgroundColor);
        // Default accent-derived colors to background_color
        element.style.setProperty('--color-link', backgroundColor);
        element.style.setProperty('--color-rating-background', backgroundColor);
        element.style.setProperty('--color-options-foreground', backgroundColor);
        element.style.setProperty('--color-options-border', backgroundColor);
        try {
            element.style.setProperty('--moveo-background-color-hover', shader(backgroundColor, -0.3));
        }
        catch (_a) {
            // Ignore shader errors for invalid colors
        }
    }
    if (accentColor) {
        element.style.setProperty('--moveo-accent-color', accentColor);
        element.style.setProperty('--color-text-primary-foreground', accentColor);
    }
    // 2. Apply theme_colors from backend config (overrides legacy)
    if (themeColors) {
        Object.entries(themeColorsToCssVars).forEach(([configKey, cssVar]) => {
            const value = themeColors[configKey];
            if (value) {
                element.style.setProperty(cssVar, value);
                if (configKey === 'accent_color') {
                    element.style.setProperty('--moveo-accent-color', value);
                    element.style.setProperty('--color-options-border', value);
                    element.style.setProperty('--color-options-foreground', value);
                    element.style.setProperty('--color-link', value);
                    element.style.setProperty('--color-rating-background', value);
                }
                if (configKey === 'background_color') {
                    element.style.setProperty('--moveo-background-color', value);
                    try {
                        element.style.setProperty('--moveo-background-color-hover', shader(value, -0.3));
                    }
                    catch (_a) {
                        // Ignore shader errors for invalid colors
                    }
                }
            }
        });
    }
    // 3. Apply runtime CSS overrides (highest priority)
    if (cssOverrides) {
        Object.entries(cssOverrides).forEach(([cssVar, value]) => {
            if (value) {
                // Map semantic font sizes to pixel values
                if (cssVar === '--font-size-brain-message') {
                    const mappedValue = brainMessageFontSizes[value.toLowerCase()] || value;
                    element.style.setProperty(cssVar, mappedValue);
                }
                else {
                    element.style.setProperty(cssVar, value);
                }
            }
        });
    }
};
export const useTheme = () => {
    const backgroundColor = useRecoilValue(backgroundColorSelector);
    const accentColor = useRecoilValue(accentColorSelector);
    const fontFamily = useRecoilValue(fontFamilySelector);
    const [widgetConfig, setWidgetConfig] = useRecoilState(userConfigState);
    const chatWindowSize = useRecoilValue(chatWindowSizeSelector);
    const themeColors = useRecoilValue(themeColorsSelector);
    const cssOverrides = useRecoilValue(currentThemeOverridesSelector);
    const element = document.querySelector('.appContainer') ||
        document.querySelector('body');
    const { isNbg, isAlpha } = useRecoilValue(variantSelector);
    const loadFont = useCallback((fontFamily, fontPath, fontWeight = 'normal', fontStyle = 'normal') => {
        const fontFace = new FontFace(fontFamily, `url(${fontPath})`, {
            weight: fontWeight,
            style: fontStyle,
        });
        fontFace.load().then((loadedFont) => {
            document.fonts.add(loadedFont);
            // Set default-font-family as primary for Tailwind/shadcn
            element === null || element === void 0 ? void 0 : element.style.setProperty('--default-font-family', `'${fontFamily}', sans-serif`);
            // Keep moveo-font-primary for backward compatibility
            element === null || element === void 0 ? void 0 : element.style.setProperty('--moveo-font-primary', `'${fontFamily}', sans-serif`);
        });
    }, [element === null || element === void 0 ? void 0 : element.style]);
    const loadGoogleFont = useCallback((fontFamily, fontPath) => {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = fontPath;
        document.head.appendChild(link);
        // Set both variables for compatibility
        element === null || element === void 0 ? void 0 : element.style.setProperty('--default-font-family', `'${fontFamily}', sans-serif`);
        element === null || element === void 0 ? void 0 : element.style.setProperty('--moveo-font-primary', `'${fontFamily}', sans-serif`);
    }, [element === null || element === void 0 ? void 0 : element.style]);
    useEffect(() => {
        const initFont = async () => {
            try {
                // Load Google Font if specified in config
                if (fontFamily) {
                    const fontPath = googleFontPath(fontFamily);
                    loadGoogleFont(fontFamily, fontPath);
                    // Load NBG Font if variant is nbg
                }
                else if (isNbg) {
                    for (const font of configs.nbg) {
                        loadFont(font.fontFamily, font.fontPath, font.fontWeight, font.fontStyle);
                    }
                    // Load Alpha Font if variant is alpha
                }
                else if (isAlpha) {
                    for (const font of configs.alpha) {
                        loadFont(font.fontFamily, font.fontPath, font.fontWeight, font.fontStyle);
                    }
                    // Load default font
                }
                else {
                    loadGoogleFont(configs.default.fontFamily, configs.default.fontPath);
                }
            }
            catch (_a) {
                loadGoogleFont(configs.default.fontFamily, configs.default.fontPath);
            }
        };
        initFont();
    }, [fontFamily, loadFont, loadGoogleFont, isNbg, isAlpha]);
    useEffect(() => {
        // Apply default variables
        Object.entries(variables).forEach(([key, value]) => {
            // If we have a custom font and this is the font-primary variable, use the custom font
            if (key !== '--moveo-font-primary') {
                element === null || element === void 0 ? void 0 : element.style.setProperty(key, value);
            }
        });
        if (chatWindowSize) {
            element === null || element === void 0 ? void 0 : element.style.setProperty('--moveo-chat-width', chatWindowSize === null || chatWindowSize === void 0 ? void 0 : chatWindowSize.width);
            element === null || element === void 0 ? void 0 : element.style.setProperty('--moveo-chat-height', chatWindowSize === null || chatWindowSize === void 0 ? void 0 : chatWindowSize.height);
        }
    }, [chatWindowSize, element === null || element === void 0 ? void 0 : element.style]);
    // Handle auto theme detection
    useEffect(() => {
        if (widgetConfig.theme === 'auto') {
            const isDark = window.matchMedia &&
                window.matchMedia('(prefers-color-scheme: dark)').matches;
            setWidgetConfig((prev) => (Object.assign(Object.assign({}, prev), { theme: isDark ? 'dark' : 'light' })));
        }
    }, [setWidgetConfig, widgetConfig.theme]);
    // Apply CSS variables
    useEffect(() => {
        if (!element)
            return;
        applyCssVariables({
            element,
            backgroundColor,
            accentColor,
            themeColors,
            cssOverrides,
        });
    }, [accentColor, backgroundColor, cssOverrides, element, themeColors]);
};
